/**
 * Purchase error translation.
 *
 * A user cancelling checkout is not a failure, and must never be shown as one.
 */

export class SubscriptionError extends Error {
  constructor(code, message, { cancelled = false, recoverable = true, cause } = {}) {
    super(message);
    this.name = 'SubscriptionError';
    this.code = code;
    this.cancelled = cancelled;
    this.recoverable = recoverable;
    if (cause) this.cause = cause;
  }
}

/** Maps an SDK purchase error onto a SubscriptionError with usable copy. */
export function translatePurchaseError(error) {
  if (error instanceof SubscriptionError) return error;

  const code = String(error?.errorCode ?? error?.code ?? '').toUpperCase();
  const message = String(error?.message ?? '');

  if (
    code.includes('USER_CANCELLED') ||
    code.includes('USERCANCELLED') ||
    /cancell?ed/i.test(message)
  ) {
    return new SubscriptionError('PURCHASE_CANCELLED', 'Checkout was cancelled.', {
      cancelled: true,
      cause: error,
    });
  }

  if (code.includes('ALREADY_PURCHASED') || code.includes('PRODUCT_ALREADY')) {
    return new SubscriptionError(
      'ALREADY_SUBSCRIBED',
      'You already have an active subscription. Try refreshing your subscription status.',
      { cause: error },
    );
  }

  if (code.includes('PAYMENT_PENDING') || code.includes('PENDING')) {
    return new SubscriptionError(
      'PURCHASE_PENDING',
      'Your payment is still processing. Premium will unlock as soon as it completes.',
      { cause: error },
    );
  }

  if (code.includes('NETWORK')) {
    return new SubscriptionError(
      'NETWORK_ERROR',
      'We could not reach the payment service. Check your connection and try again.',
      { cause: error },
    );
  }

  if (code.includes('STORE_PROBLEM') || code.includes('UNSUPPORTED')) {
    return new SubscriptionError(
      'BILLING_UNAVAILABLE',
      'Payments are temporarily unavailable. Please try again shortly.',
      { cause: error },
    );
  }

  if (code.includes('INVALID_CREDENTIALS') || code.includes('CONFIGURATION')) {
    return new SubscriptionError(
      'BILLING_MISCONFIGURED',
      'Payments are not set up correctly on this deployment.',
      { recoverable: false, cause: error },
    );
  }

  return new SubscriptionError(
    'PURCHASE_FAILED',
    'We could not complete that purchase. You have not been charged.',
    { cause: error },
  );
}
